import { useState } from "react";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import { Card, Icon } from "@bfc-software/pallet";

import type {
  Counted,
  NotActivated,
  Pickers,
  PickersDetail,
  TaskProgress,
} from "../-data/dashboard-model";
import type { DashboardT } from "./dashboard-t";
import { PickersDrawer } from "./pickers-drawer";
import styles from "./dashboard.module.css";

function Figure({
  value,
  unit,
  role,
  tone,
}: {
  value: number | string;
  unit: string;
  role: string;
  tone?: "warning" | "success";
}) {
  return (
    <div className={styles.statFigure}>
      <span
        className={clsx(
          styles.statValue,
          tone === "warning" && styles.statValueWarning,
          tone === "success" && styles.statValueSuccess,
        )}
        data-test-role={role}
      >
        {value}
      </span>
      <span className={styles.statUnit}>{unit}</span>
    </div>
  );
}

/**
 * The three cards under the hero: work on the floor, work not yet released, and
 * who is available to do it.
 *
 * "Activated" and "Not activated" are links into the Tasks page, each landing on
 * the view that answers its number:
 *
 * - Activated is the released work that is not finished, which is the All tasks
 *   tab's default scope (`open`). The plans tab cannot say it in one view - it
 *   offers "not started" and "in progress" separately - so the task list is the
 *   faithful target for a count of tasks.
 * - Not activated is the plans tab's default (`not-activated`), where each
 *   plan's own Activate button is.
 *
 * Whole-card links rather than a button inside a card, so the figures are as
 * clickable as the words.
 *
 * Pickers opens a drawer instead of navigating, so the whole card is a pressable
 * `Card` rather than a link.
 */
export function StatCards({
  params,
  tasks,
  activatedRoutes,
  notActivated,
  pickers,
  pickersDetail,
  t,
}: {
  /** The route's own params, which the Tasks page needs to be addressed. */
  params: { lang: string; org: string; location: string };
  tasks: TaskProgress;
  activatedRoutes: Counted;
  notActivated: NotActivated;
  pickers: Pickers;
  pickersDetail: PickersDetail;
  t: DashboardT;
}) {
  const [pickersOpen, setPickersOpen] = useState(false);

  return (
    <div className={styles.stats}>
      <Link
        to="/$lang/orgs/$org/outbound/whs/$location/tasks/all"
        params={params}
        className={styles.statLink}
        data-test-role="dashboard-activated-link"
      >
        <Card
          variant="stat"
          layout="body-only"
          data-test-role="dashboard-activated"
          body={
            <>
              <span className={styles.statLabel}>
                {t("dashboard.activated.title")}
              </span>
              <div className={styles.statFigures}>
                <Figure
                  value={
                    activatedRoutes.capped
                      ? `${activatedRoutes.count}+`
                      : activatedRoutes.count
                  }
                  unit={t("dashboard.activated.routes")}
                  role="activated-routes"
                />
                <Figure
                  value={tasks.remaining}
                  unit={t("dashboard.activated.tasks")}
                  role="activated-tasks"
                />
              </div>
              <span className={styles.statDetail}>
                {t("dashboard.activated.detail", {
                  inProgress: tasks.inProgress,
                  waiting: tasks.waiting,
                })}
              </span>
            </>
          }
        />
      </Link>
      <Link
        to="/$lang/orgs/$org/outbound/whs/$location/tasks/plans"
        params={params}
        className={styles.statLink}
        data-test-role="dashboard-not-activated-link"
      >
        <Card
          variant="stat-warning"
          layout="body-only"
          data-test-role="dashboard-not-activated"
          body={
            <>
              <span className={styles.statLabel}>
                {t("dashboard.notActivated.title")}
              </span>
              <div className={styles.statFigures}>
                <Figure
                  value={
                    notActivated.capped
                      ? `${notActivated.routes}+`
                      : notActivated.routes
                  }
                  unit={t("dashboard.notActivated.routes")}
                  tone="warning"
                  role="not-activated-routes"
                />
                <Figure
                  value={
                    notActivated.capped
                      ? `${notActivated.tasks}+`
                      : notActivated.tasks
                  }
                  unit={t("dashboard.notActivated.tasks")}
                  tone="warning"
                  role="not-activated-tasks"
                />
              </div>
              <span className={styles.statAction}>
                {t("dashboard.notActivated.review")}
              </span>
            </>
          }
        />
      </Link>
      <Card
        variant="stat"
        layout="body-only"
        data-test-role="dashboard-pickers"
        onPress={() => setPickersOpen(true)}
        pressLabel={t("dashboard.pickers.view")}
        body={
          <>
            <div className={styles.statHead}>
              <span className={styles.statLabel}>
                {t("dashboard.pickers.title")}
              </span>
              <Icon name="Users" size={16} />
            </div>
            <div className={styles.statFigures}>
              <Figure
                value={pickers.active}
                unit={t("dashboard.pickers.active")}
                tone="success"
                role="pickers-active"
              />
              <Figure
                value={pickers.idle}
                unit={t("dashboard.pickers.idle")}
                role="pickers-idle"
              />
            </div>
          </>
        }
      />
      {pickersOpen && (
        <PickersDrawer
          detail={pickersDetail}
          onClose={() => setPickersOpen(false)}
          t={t}
        />
      )}
    </div>
  );
}
